<script lang="ts" setup>
import type { InputDateProps } from '@nuxt/ui';
import type { Temporal } from 'temporal-polyfill';
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import { reactivePick } from '@vueuse/core';
import DButton from '#layers/ui/components/Button.vue';
import DTooltip from '#layers/ui/components/Tooltip.vue';
import DateRangePickerBase from './DateRangePickerBase.vue';

interface Props extends Pick<ButtonProps, 'color' | 'size' | 'disabled'>, Pick<InputDateProps, 'variant'> {
	readonly?: boolean;
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const startDate = defineModel<Temporal.PlainDate | null>('startDate');
const endDate = defineModel<Temporal.PlainDate | null>('endDate');

const pickerProps = reactivePick(props, 'color', 'variant', 'size', 'disabled', 'readonly', 'block');
const triggerProps = reactivePick(props, 'size', 'disabled');
</script>

<template>
	<DateRangePickerBase
		v-bind="pickerProps"
		v-model:start-date="startDate"
		v-model:end-date="endDate"
	>
		<template #trigger="{ rangeFieldDisplay, canClearRange, clearRange }">
			<DButton
				v-bind="triggerProps"
				type="button"
				leading-icon="lucide:calendar"
				:trailing-icon="!props.readonly ? 'lucide:chevron-down' : undefined"
				color="neutral"
				variant="outline"
				data-test-id="date-range-trigger"
				:block
				:readonly="props.readonly"
				:aria-readonly="props.readonly"
				:ui="{
					base: 'justify-between font-medium',
					label: 'flex-1 truncate text-start',
				}"
				:class="{ 'cursor-pointer': !props.readonly && !disabled }"
			>
				<span class="flex-1 truncate text-start">{{ rangeFieldDisplay }}</span>

				<template v-if="canClearRange" #trailing>
					<DTooltip :text="$t('common.clear')">
						<DButton
							type="button"
							class="p-0 text-dimmed"
							color="neutral"
							variant="link"
							icon="lucide:x"
							size="sm"
							data-test-id="date-range-clear-range"
							:disabled
							@click.stop="clearRange"
							@pointerdown.stop
						/>
					</DTooltip>
				</template>
			</DButton>
		</template>
	</DateRangePickerBase>
</template>
